/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

(() => {
  const DESKTOP_BREAKPOINT = 1000;
  const CLOSE_DELAY = 120;
  const closeTimers = new WeakMap();
  const popovers = new Set();

  function isDesktopViewport() {
    return window.innerWidth >= DESKTOP_BREAKPOINT;
  }

  function getParts(root) {
    return {
      trigger: root.querySelector('[data-account-trigger]'),
      panel: root.querySelector('[data-account-panel]')
    };
  }

  function clearCloseTimer(root) {
    const timerId = closeTimers.get(root);

    if (timerId) {
      window.clearTimeout(timerId);
      closeTimers.delete(root);
    }
  }

  function setOpenState(root, isOpen, restoreFocus) {
    const { trigger, panel } = getParts(root);

    if (!trigger || !panel) {
      return;
    }

    clearCloseTimer(root);
    root.classList.toggle('is-open', isOpen);
    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

    if (!isOpen && restoreFocus) {
      trigger.focus();
    }
  }

  function openPopover(root) {
    if (!isDesktopViewport()) {
      return;
    }

    setOpenState(root, true, false);
  }

  function closePopover(root, restoreFocus) {
    setOpenState(root, false, restoreFocus);
  }

  function scheduleClose(root) {
    clearCloseTimer(root);

    closeTimers.set(root, window.setTimeout(() => {
      closePopover(root, false);
      closeTimers.delete(root);
    }, CLOSE_DELAY));
  }

  function bindPopover(root) {
    if (root.dataset.accountPopoverBound === 'true') {
      return;
    }

    const { trigger, panel } = getParts(root);

    if (!trigger || !panel) {
      return;
    }

    root.dataset.accountPopoverBound = 'true';
    popovers.add(root);

    root.addEventListener('mouseenter', () => {
      openPopover(root);
    });

    root.addEventListener('mouseleave', (event) => {
      if (!root.contains(event.relatedTarget)) {
        scheduleClose(root);
      }
    });

    root.addEventListener('focusin', () => {
      openPopover(root);
    });

    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) {
        scheduleClose(root);
      }
    });
  }

  function initAccountPopovers(container) {
    container.querySelectorAll('[data-account-popover]').forEach(bindPopover);
  }

  document.addEventListener('click', (event) => {
    popovers.forEach((root) => {
      if (!root.isConnected) {
        popovers.delete(root);
        closeTimers.delete(root);
        return;
      }

      if (!root.contains(event.target)) {
        closePopover(root, false);
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    popovers.forEach((root) => {
      if (!root.isConnected) {
        popovers.delete(root);
        closeTimers.delete(root);
        return;
      }

      if (root.classList.contains('is-open')) {
        event.preventDefault();
        closePopover(root, root.contains(document.activeElement));
      }
    });
  });

  window.addEventListener('resize', () => {
    if (isDesktopViewport()) {
      return;
    }

    popovers.forEach((root) => {
      if (!root.isConnected) {
        popovers.delete(root);
        closeTimers.delete(root);
        return;
      }

      closePopover(root, false);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAccountPopovers(document);
    });
  } else {
    initAccountPopovers(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAccountPopovers(event.target);
  });
})();

(() => {
  const MOBILE_BREAKPOINT = 740;
  const benefitSliderSections = new Set();

  function isMobileViewport() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function getBenefitSliderParts(section) {
    const strip = section.querySelector('.air-benefits-strip--mobile-swipe');

    if (!strip) {
      return {};
    }

    return {
      strip,
      list: strip.querySelector('[data-air-benefits-list]'),
      items: Array.from(strip.querySelectorAll('.air-benefits-strip__item')),
      prevButton: strip.querySelector('[data-air-benefits-prev]'),
      nextButton: strip.querySelector('[data-air-benefits-next]')
    };
  }

  function getClosestItemIndex(list, items) {
    if (!list || items.length === 0) {
      return 0;
    }

    const listCenter = list.scrollLeft + list.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    items.forEach((item, index) => {
      const itemCenter = item.offsetLeft + item.clientWidth / 2;
      const distance = Math.abs(itemCenter - listCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  function updateBenefitSliderControls(section) {
    const state = section._airBenefitsSlider;

    if (!state) {
      return;
    }

    const { list, items, prevButton, nextButton } = state;
    const activeIndex = getClosestItemIndex(list, items);
    const disableControls = !isMobileViewport() || items.length <= 1;

    state.activeIndex = activeIndex;

    if (prevButton) {
      const isDisabled = disableControls || activeIndex <= 0;
      prevButton.disabled = isDisabled;
      prevButton.classList.toggle('is-disabled', isDisabled);
    }

    if (nextButton) {
      const isDisabled = disableControls || activeIndex >= items.length - 1;
      nextButton.disabled = isDisabled;
      nextButton.classList.toggle('is-disabled', isDisabled);
    }
  }

  function scrollBenefitSliderToIndex(section, targetIndex, behavior) {
    const state = section._airBenefitsSlider;

    if (!state) {
      return;
    }

    const { list, items } = state;
    const nextIndex = clamp(targetIndex, 0, items.length - 1);
    const targetItem = items[nextIndex];

    if (!targetItem) {
      return;
    }

    const targetLeft = Math.max(0, targetItem.offsetLeft - (list.clientWidth - targetItem.clientWidth) / 2);

    state.activeIndex = nextIndex;
    list.scrollTo({ left: targetLeft, behavior: behavior || 'smooth' });
    window.requestAnimationFrame(() => updateBenefitSliderControls(section));
  }

  function bindBenefitSlider(section) {
    if (section.dataset.airBenefitsSliderBound === 'true') {
      return;
    }

    const { list, items, prevButton, nextButton } = getBenefitSliderParts(section);

    if (!list || items.length <= 1 || !prevButton || !nextButton) {
      return;
    }

    section.dataset.airBenefitsSliderBound = 'true';
    section._airBenefitsSlider = {
      list,
      items,
      prevButton,
      nextButton,
      activeIndex: 0,
      ticking: false
    };

    benefitSliderSections.add(section);

    prevButton.addEventListener('click', () => {
      if (!isMobileViewport()) {
        return;
      }

      scrollBenefitSliderToIndex(section, section._airBenefitsSlider.activeIndex - 1);
    });

    nextButton.addEventListener('click', () => {
      if (!isMobileViewport()) {
        return;
      }

      scrollBenefitSliderToIndex(section, section._airBenefitsSlider.activeIndex + 1);
    });

    list.addEventListener('scroll', () => {
      const state = section._airBenefitsSlider;

      if (!state || state.ticking) {
        return;
      }

      state.ticking = true;

      window.requestAnimationFrame(() => {
        state.ticking = false;
        updateBenefitSliderControls(section);
      });
    }, { passive: true });

    if (isMobileViewport()) {
      scrollBenefitSliderToIndex(section, 0, 'auto');
    } else {
      updateBenefitSliderControls(section);
    }
  }

  function initBenefitSliders(container) {
    container.querySelectorAll('[data-section-type="air-benefits-strip"]').forEach(bindBenefitSlider);
  }

  window.addEventListener('resize', () => {
    benefitSliderSections.forEach((section) => {
      if (!section.isConnected) {
        benefitSliderSections.delete(section);
        return;
      }

      if (isMobileViewport()) {
        scrollBenefitSliderToIndex(section, section._airBenefitsSlider ? section._airBenefitsSlider.activeIndex : 0, 'auto');
      } else {
        updateBenefitSliderControls(section);
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initBenefitSliders(document);
    });
  } else {
    initBenefitSliders(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initBenefitSliders(event.target);
  });
})();
